import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiErrorMessage, resetPassword } from '@/api/client';
import { AuthShell } from '@/components/auth/AuthShell';
import { usePageMeta } from '@/hooks/use-page-meta';

export function ResetPassword() {
  usePageMeta("Choose a new password — Aura", { noindex: true })
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        title="Invalid reset link"
        footer={
          <span>
            Need a new one?{' '}
            <Link to="/forgot-password" className="">
              Request a reset link
            </Link>
          </span>
        }
      >
        <p className="auth-status tone-error">
          This page was opened without a reset token. Request a fresh link from the forgot-password page.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Your new password must be at least 8 characters."
      footer={
        <span>
          <Link to="/login" className="">
            Back to sign in
          </Link>
        </span>
      }
    >
      {done ? (
        <p className="auth-status tone-success aura-panel-in">
          Password updated. Redirecting you to sign in…
        </p>
      ) : (
        <>
          {error && (
            <div className="auth-banner auth-banner-error" role="alert">{error}</div>
          )}
          <form onSubmit={onSubmit} className="auth-form">
            <label className="auth-field">
              <span>New password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className="auth-field">
              <span>Confirm new password</span>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
